// src/pages/analytics/AudienceAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, Chip, useTheme, alpha,
  Breadcrumbs, Link, Tab, Tabs, CircularProgress, Button,
  MenuItem, Select, FormControl, InputLabel, Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Group as AudienceIcon,
  Public as GeoIcon,
  Person as DemographicIcon,
  DevicesOther as DeviceIcon,
  Schedule as TimeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

// Import custom components
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import MetricComparison from '../../components/features/analytics/MetricComparison';
import ExportButton from '../../components/features/analytics/ExportButton';
import AnalyticsFilters from '../../components/features/analytics/AnalyticsFilters';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportAnalyticsData } from '../../services/analyticsService';

// Import utils
import { formatNumber } from '../../utils/analyticsUtils';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
} from '../../styles/analyticsResponsive';

/**
 * Audience Analytics Page Component
 */
const AudienceAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [platformFilter, setPlatformFilter] = useState('all');
  
  // Define filter state
  const [filters, setFilters] = useState({
    platform: [],
    ageGroup: 'all',
    gender: 'all',
    location: 'all',
    deviceType: 'all',
  });
  
  const { 
    audienceAnalytics, isLoading, criticalError, fetchAudienceAnalytics,
    dateRange, setDateRange, clearError,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Define filter options
  const filterOptions = [
    {
      key: 'platform',
      label: 'Platform',
      type: 'multi-select',
      options: [
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'tiktok', label: 'TikTok' },
      ],
      defaultValue: [],
      helperText: 'Filter by social media platform',
    },
    {
      key: 'ageGroup',
      label: 'Age Group',
      type: 'select',
      options: [
        { value: 'all', label: 'All Ages' },
        { value: '18-24', label: '18-24' },
        { value: '25-34', label: '25-34' },
        { value: '35-44', label: '35-44' },
        { value: '45-54', label: '45-54' },
        { value: '55+', label: '55+' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'gender',
      label: 'Gender',
      type: 'select',
      options: [
        { value: 'all', label: 'All Genders' },
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'location',
      label: 'Location',
      type: 'select',
      options: [
        { value: 'all', label: 'All Locations' },
        { value: 'usa', label: 'United States' },
        { value: 'europe', label: 'Europe' },
        { value: 'asia', label: 'Asia' },
        { value: 'middleeast', label: 'Middle East' },
        { value: 'africa', label: 'Africa' },
      ],
      defaultValue: 'all',
    },
  ];
  
  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAudienceAnalytics({
          platforms: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
          ageGroup: filters.ageGroup !== 'all' ? filters.ageGroup : undefined,
          gender: filters.gender !== 'all' ? filters.gender : undefined,
          location: filters.location !== 'all' ? filters.location : undefined,
          deviceType: filters.deviceType !== 'all' ? filters.deviceType : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        });
      } catch (error) {
        showError(`Failed to load audience analytics: ${error.message || 'Unknown error'}`);
      }
    };
    
    loadData();
  }, [fetchAudienceAnalytics, showError, dateRange, filters]);
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Prepare export params
      const exportParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        // Include filter parameters
        platforms: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
        ageGroup: filters.ageGroup !== 'all' ? filters.ageGroup : undefined,
        gender: filters.gender !== 'all' ? filters.gender : undefined,
        location: filters.location !== 'all' ? filters.location : undefined,
        reportType: 'audience',
      };
      
      // Call the export service
      await exportAnalyticsData(exportParams);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Check if data is available
  const hasData = !isLoading && audienceAnalytics;
  
  // Loading state
  if (isLoading && !audienceAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !audienceAnalytics) {
    return (
      <Box sx={{ py: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Audience Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => {
                  clearError();
                  fetchAudienceAnalytics();
                }}
              >
                Retry
              </Button>
            }
          >
            {criticalError}
          </Alert>
        </Box>
      </Box>
    );
  }
  
  // Mock data for development - replace with real data from API
  const demographicData = [
    { ageGroup: '18-24', male: 20, female: 35, other: 5 },
    { ageGroup: '25-34', male: 35, female: 40, other: 3 },
    { ageGroup: '35-44', male: 25, female: 20, other: 2 },
    { ageGroup: '45-54', male: 15, female: 18, other: 1 },
    { ageGroup: '55+', male: 10, female: 12, other: 1 },
  ];
  
  const locationData = [
    { name: 'United States', value: 45 },
    { name: 'Europe', value: 25 },
    { name: 'Middle East', value: 15 },
    { name: 'Asia', value: 10 },
    { name: 'Other', value: 5 },
  ];
  
  const deviceData = [
    { name: 'Mobile', value: 68 },
    { name: 'Desktop', value: 25 },
    { name: 'Tablet', value: 7 },
  ];
  
  const audienceGrowthData = [
    { month: 'Jan', followers: 10000, engagement: 2500 },
    { month: 'Feb', followers: 12000, engagement: 3000 },
    { month: 'Mar', followers: 15000, engagement: 4500 },
    { month: 'Apr', followers: 18000, engagement: 5700 },
    { month: 'May', followers: 20000, engagement: 6500 },
    { month: 'Jun', followers: 22000, engagement: 7200 },
  ];
  
  const audienceInterestData = [
    { category: 'Technology', interest: 75 },
    { category: 'Travel', interest: 60 },
    { category: 'Fashion', interest: 45 },
    { category: 'Food', interest: 80 },
    { category: 'Fitness', interest: 55 },
    { category: 'Business', interest: 65 },
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Audience Analytics</Typography>
        </Breadcrumbs>
        
        <Box sx={getPageHeaderStyles(theme)}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Audience Analytics
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <AnalyticsFilters
              filters={filters}
              onFiltersChange={handleFilterChange}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              filterOptions={filterOptions}
              showDateRange={true}
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Error message */}
      {criticalError && (
        <Alert 
          severity="error" 
          onClose={() => clearError()}
          sx={{ mb: 4 }}
        >
          {criticalError}
        </Alert>
      )}
      
      {/* Overview KPIs */}
      <Grid container {...getKpiGridProps(theme).container}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Audience"
            value={245000}
            previousValue={220000}
            percentChange={11.4}
            icon={<AudienceIcon />}
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="New Followers"
            value={15200}
            previousValue={12800}
            percentChange={18.7}
            icon={<TrendingUpIcon />}
            color="secondary"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Engagement Rate"
            value={4.7}
            unit="%"
            previousValue={4.2}
            percentChange={11.9}
            icon={<TrendingUpIcon />}
            color="success"
            isLoading={isLoading}
            magical
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Avg. Session Time"
            value={"3:45"}
            unit=""
            previousValue={"3:12"}
            percentChange={16.9}
            icon={<TimeIcon />}
            color="info"
            isLoading={isLoading}
            magical
          />
        </Grid>
      </Grid>
      
      {/* Content tabs */}
      <Box sx={getTabsContainerStyles(theme)}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<AudienceIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<DemographicIcon />} iconPosition="start" label="Demographics" />
          <Tab icon={<GeoIcon />} iconPosition="start" label="Geographic" />
          <Tab icon={<DeviceIcon />} iconPosition="start" label="Devices & Interests" />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Audience Growth"
              subtitle="Followers and engagement over time"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={audienceGrowthData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="followers"
                    name="Followers"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="engagement"
                    name="Engagement"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <ChartCard
              title="Audience Demographics"
              subtitle="Gender & age distribution"
              icon={<DemographicIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={demographicData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis dataKey="ageGroup" type="category" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  <Legend />
                  <Bar dataKey="male" name="Male" fill={theme.palette.primary.main} />
                  <Bar dataKey="female" name="Female" fill={theme.palette.secondary.main} />
                  <Bar dataKey="other" name="Other" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={6}>
            <ChartCard
              title="Geographic Distribution"
              subtitle="Audience by location"
              icon={<GeoIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={locationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {locationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={6}>
            <ChartCard
              title="Audience Interests"
              subtitle="Interest categories"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              color="info"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={audienceInterestData}>
                  <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                  <PolarAngleAxis dataKey="category" tick={{ fill: theme.palette.text.secondary }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: theme.palette.text.secondary }} />
                  <Radar
                    name="Interest Level"
                    dataKey="interest"
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.2)}
                    fillOpacity={0.6}
                  />
                  <Legend />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Demographics Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Age Distribution"
              subtitle="Audience by age group"
              icon={<DemographicIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={demographicData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="ageGroup" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  <Legend />
                  <Bar dataKey="male" name="Male" fill={theme.palette.primary.main} />
                  <Bar dataKey="female" name="Female" fill={theme.palette.secondary.main} />
                  <Bar dataKey="other" name="Other" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Gender Distribution"
              subtitle="Audience by gender"
              icon={<DemographicIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Male', value: 42 },
                      { name: 'Female', value: 55 },
                      { name: 'Other', value: 3 },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    <Cell fill={theme.palette.primary.main} />
                    <Cell fill={theme.palette.secondary.main} />
                    <Cell fill={theme.palette.info.main} />
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Demographics by Platform"
              subtitle="Audience composition across platforms"
              icon={<DemographicIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { platform: 'Facebook', '18-24': 20, '25-34': 35, '35-44': 25, '45-54': 15, '55+': 5 },
                    { platform: 'Instagram', '18-24': 45, '25-34': 30, '35-44': 15, '45-54': 7, '55+': 3 },
                    { platform: 'Twitter', '18-24': 30, '25-34': 35, '35-44': 20, '45-54': 10, '55+': 5 },
                    { platform: 'LinkedIn', '18-24': 10, '25-34': 40, '35-44': 30, '45-54': 15, '55+': 5 },
                    { platform: 'YouTube', '18-24': 35, '25-34': 30, '35-44': 20, '45-54': 10, '55+': 5 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="platform" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  <Legend />
                  <Bar dataKey="18-24" fill={chartColors[0]} />
                  <Bar dataKey="25-34" fill={chartColors[1]} />
                  <Bar dataKey="35-44" fill={chartColors[2]} />
                  <Bar dataKey="45-54" fill={chartColors[3]} />
                  <Bar dataKey="55+" fill={chartColors[4]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Geographic Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Audience by Region"
              subtitle="Geographic distribution"
              icon={<GeoIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={locationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {locationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Top Cities"
              subtitle="Audience by city"
              icon={<GeoIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  layout="vertical"
                  data={[
                    { city: 'New York', value: 18 },
                    { city: 'London', value: 15 },
                    { city: 'Dubai', value: 12 },
                    { city: 'Paris', value: 10 },
                    { city: 'Tokyo', value: 8 },
                    { city: 'Berlin', value: 7 },
                    { city: 'Sydney', value: 6 },
                    { city: 'Toronto', value: 5 },
                  ]}
                  margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis dataKey="city" type="category" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, 'Audience']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Bar dataKey="value" fill={theme.palette.secondary.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Language Distribution"
              subtitle="Audience by language preference"
              icon={<GeoIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { language: 'English', value: 65 },
                    { language: 'Arabic', value: 15 },
                    { language: 'Spanish', value: 8 },
                    { language: 'French', value: 5 },
                    { language: 'German', value: 4 },
                    { language: 'Others', value: 3 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="language" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, 'Audience']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Bar dataKey="value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Devices & Interests Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <ChartCard
              title="Device Usage"
              subtitle="Sessions by device type"
              icon={<DeviceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    <Cell fill={theme.palette.primary.main} />
                    <Cell fill={theme.palette.secondary.main} />
                    <Cell fill={theme.palette.info.main} />
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} md={6} lg={8}>
            <ChartCard
              title="Audience Interests"
              subtitle="Top interest categories"
              icon={<TrendingUpIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={audienceInterestData}>
                  <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                  <PolarAngleAxis dataKey="category" tick={{ fill: theme.palette.text.secondary }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: theme.palette.text.secondary }} />
                  <Radar
                    name="Interest Level"
                    dataKey="interest"
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.2)}
                    fillOpacity={0.6}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, 'Interest Level']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Platform Usage by Device"
              subtitle="Device distribution by platform"
              icon={<DeviceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { platform: 'Facebook', Mobile: 65, Desktop: 30, Tablet: 5 },
                    { platform: 'Instagram', Mobile: 85, Desktop: 10, Tablet: 5 },
                    { platform: 'Twitter', Mobile: 70, Desktop: 25, Tablet: 5 },
                    { platform: 'LinkedIn', Mobile: 45, Desktop: 50, Tablet: 5 },
                    { platform: 'YouTube', Mobile: 60, Desktop: 35, Tablet: 5 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="platform" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="Mobile" stackId="a" fill={theme.palette.primary.main} />
                  <Bar dataKey="Desktop" stackId="a" fill={theme.palette.secondary.main} />
                  <Bar dataKey="Tablet" stackId="a" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AudienceAnalytics;